import { type ReactNode } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { IconFile } from '@tabler/icons-react';
import { showsAgentLicenseNumber, showsMlsOfficeId } from 'components/offerPortal/listingDetails';
import { isLoanFinancing } from 'components/offerPortal/offerSubmission';
import { formatPhoneNumber } from 'helpers/formatters';
import { type OfferPortalWizardContext, WizardStep } from 'state_machines/OfferPortalWizardMachine';

import { financingDocumentLabel, financingDocumentPurpose } from './financingDocument';
import {
  CARD_TITLE,
  CONTINGENT_STATUS_OPTIONS,
  DOCUMENT_ROW,
  FIELD_CARD,
  FINANCING_OPTIONS,
  formatFileSize,
  LENDER_OPTIONS,
  type Send,
} from './wizardFields';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const contingencyLabel = (value: boolean | null | undefined): string => {
  if (value == null) return '-';
  return value ? 'Yes' : 'Waived';
};

const ReviewRow = ({ label, value }: { label: string; value: string }) => (
  <Row className="justify-between gap-4">
    <Text className="text-body-md text-od-content-secondary">{label}</Text>
    <Text className="text-body-md text-od-content-primary">{value}</Text>
  </Row>
);

const ReviewSection = ({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: ReactNode;
}) => (
  <Col className={FIELD_CARD}>
    <Row className="justify-between">
      <Text as="h2" className={CARD_TITLE}>
        {title}
      </Text>
      <Button
        type="button"
        variant="secondary"
        onClick={onEdit}
        className="h-auto border-0 bg-transparent p-0 text-label-md !text-od-content-accent-brand not-disabled:hover:bg-transparent"
        aria-label={`Edit ${title}`}
        analyticsName="cosmos-make-offer-review-edit"
      >
        Edit
      </Button>
    </Row>
    {children}
  </Col>
);

/** A waived (0-day) period reads as such rather than a bare zero. */
const daysLabel = (days: number | null): string => {
  if (days == null) {
    return '-';
  }
  return days === 0 ? 'Waived' : `${days} days`;
};

/** Step 6 of 6 of the offer wizard — every answer read back, editable per section. */
const ReviewStep = ({ context, send }: { context: OfferPortalWizardContext; send: Send }) => {
  // The machine only keeps the financing document the current financing type
  // calls for, so everything else is a contract or extra document.
  const purpose = financingDocumentPurpose(context.financingType);
  const financingDocument = context.documents.find((document) => document.purpose === purpose);
  const otherDocuments = context.documents.filter((document) => document.purpose !== purpose);
  const handleEditDocuments = () => {
    send({ type: 'EDIT', step: WizardStep.Documents });
  };
  const pct =
    context.buyerAgentCompensationBps != null
      ? `${(context.buyerAgentCompensationBps / 100).toLocaleString('en-US')}%`
      : '-';
  return (
    <>
      <ReviewSection
        title="Offer terms"
        onEdit={() => send({ type: 'EDIT', step: WizardStep.OfferTerms })}
      >
        <ReviewRow
          label="Offer amount"
          value={context.priceCents != null ? usd.format(context.priceCents / 100) : '-'}
        />
        <ReviewRow
          label="Earnest money deposit"
          value={
            context.earnestDepositCents != null
              ? usd.format(context.earnestDepositCents / 100)
              : '-'
          }
        />
        <ReviewRow
          label="Seller concession"
          value={
            context.sellerConcessionCents != null
              ? usd.format(context.sellerConcessionCents / 100)
              : '$0'
          }
        />
        <ReviewRow label="Buyer agent commission" value={pct} />
      </ReviewSection>
      <ReviewSection
        title="Financing & closing"
        onEdit={() => send({ type: 'EDIT', step: WizardStep.Details })}
      >
        <ReviewRow
          label="Financing"
          value={
            FINANCING_OPTIONS.find((option) => option.value === context.financingType)?.label ?? '-'
          }
        />
        {isLoanFinancing(context.financingType) ? (
          <>
            <ReviewRow
              label="Down payment"
              value={
                context.downPaymentCents != null ? usd.format(context.downPaymentCents / 100) : '-'
              }
            />
            <ReviewRow
              label="Lender"
              value={
                LENDER_OPTIONS.find((option) => option.value === context.lenderType)?.label ?? '-'
              }
            />
          </>
        ) : null}
        <ReviewRow
          label="Closing date"
          value={
            context.closeDate != null
              ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                  new Date(`${context.closeDate}T00:00:00`),
                )
              : '-'
          }
        />
        <ReviewRow label="Inspection period" value={daysLabel(context.inspectionPeriodDays)} />
        <ReviewRow
          label="Financing & appraisal period"
          value={daysLabel(context.financingPeriodDays)}
        />
        <ReviewRow
          label="Home sale contingency"
          value={contingencyLabel(context.contingentOnSale)}
        />
        {context.contingentOnSale ? (
          <ReviewRow
            label="Contingent home status"
            value={
              CONTINGENT_STATUS_OPTIONS.find(
                (option) => option.value === context.contingentHomeStatus,
              )?.label ?? '-'
            }
          />
        ) : null}
      </ReviewSection>
      <ReviewSection
        title="Buyer information"
        onEdit={() => send({ type: 'EDIT', step: WizardStep.DataForDocuments })}
      >
        <ReviewRow label="Full name" value={context.buyerFullName.trim() || '-'} />
        <ReviewRow label="Email" value={context.buyerEmail.trim() || '-'} />
      </ReviewSection>
      <ReviewSection
        title="Agent information"
        onEdit={() => send({ type: 'EDIT', step: WizardStep.SubmittingAs })}
      >
        <ReviewRow
          label="Full name"
          value={`${context.agentFirstName.trim()} ${context.agentLastName.trim()}`.trim() || '-'}
        />
        <ReviewRow label="Email" value={context.agentEmail.trim() || '-'} />
        <ReviewRow
          label="Phone number"
          value={context.agentPhone ? formatPhoneNumber(context.agentPhone) : '-'}
        />
        <ReviewRow label="MLS details" value={context.agentType === 'mls' ? 'MLS' : 'Non-MLS'} />
        {context.agentType === 'mls' ? (
          <ReviewRow label="MLS ID" value={context.agentMlsId.trim() || '-'} />
        ) : null}
        {showsMlsOfficeId(context.listing, context.agentType) ? (
          <ReviewRow label="MLS office ID" value={context.agentMlsOfficeId.trim() || '-'} />
        ) : null}
        {showsAgentLicenseNumber(context.agentType) ? (
          <ReviewRow
            label="Real estate license number"
            value={context.agentLicenseNumber.trim() || '-'}
          />
        ) : null}
      </ReviewSection>
      {purpose != null ? (
        <ReviewSection title={financingDocumentLabel(purpose)} onEdit={handleEditDocuments}>
          <Text className="break-words text-body-md text-od-content-primary">
            {financingDocument?.fileName ?? 'No document attached'}
          </Text>
        </ReviewSection>
      ) : null}
      <ReviewSection title="Uploaded documents" onEdit={handleEditDocuments}>
        {otherDocuments.length === 0 ? (
          <Text className="text-body-md text-od-content-secondary">No documents attached</Text>
        ) : (
          otherDocuments.map((document) => (
            <Row key={document.documentId} className={DOCUMENT_ROW}>
              <IconFile className="size-6 shrink-0 text-od-content-primary" />
              <Col className="min-w-0 flex-1 gap-0.5">
                <Text className="truncate text-label-sm font-medium text-od-content-primary">
                  {document.fileName}
                </Text>
                <Text className="text-body-sm text-od-content-secondary">
                  {formatFileSize(document.sizeBytes)}
                </Text>
              </Col>
            </Row>
          ))
        )}
      </ReviewSection>
    </>
  );
};

export default ReviewStep;
